import React, { useState } from 'react';
import { apiUpload, AUDIENCE_WORDS } from './api.js';
import { Card, Button } from './ui.jsx';

const KINDS = [['new', 'New material'], ['change', 'A change to an existing material'], ['translation', 'A translation of an existing material']];

export default function SubmitTab({ boot, onCreated }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [audience, setAudience] = useState(['members']);
  const [useDate, setUseDate] = useState('');
  const [kind, setKind] = useState('new');
  const [parentId, setParentId] = useState('');
  const [language, setLanguage] = useState('en');
  const [practice, setPractice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [drag, setDrag] = useState(false);

  const toggle = (a) => setAudience((cur) => (cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]));
  const pick = (f) => { if (!f) return; setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, '')); };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!file) return setErr('Choose the file first.');
    if (!audience.length) return setErr('Say who receives it.');
    if (!useDate) return setErr('Give the date you want to start using it.');
    if (kind !== 'new' && !parentId) return setErr('Which existing material is this a change or translation of? Paste its ID from the Library.');
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('title', title); fd.append('audience', JSON.stringify(audience)); fd.append('desired_use_date', useDate); fd.append('kind', kind); fd.append('language', language);
    if (parentId) fd.append('parent_id', parentId);
    if (practice) fd.append('is_practice', '1');
    const d = await apiUpload('/materials', fd);
    setBusy(false);
    if (!d?.success) return setErr(d?.message || 'The upload failed.');
    // The file is handed over so the page draws it at once, while the server reads it.
    onCreated(d.material.id, file);
  };

  return (
    <form onSubmit={submit} className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
      <Card title="The material">
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
          className={`rounded-2xl border-2 border-dashed px-6 py-10 text-center transition ${drag ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-slate-50'}`}>
          {file ? (
            <div>
              <div className="text-sm font-semibold text-slate-800">{file.name}</div>
              <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(0)} KB</div>
              <button type="button" className="mt-3 text-xs text-orange-700 font-semibold" onClick={() => setFile(null)}>Choose another file</button>
            </div>
          ) : (
            <div>
              <div className="text-sm font-semibold text-slate-700">Drop the draft here</div>
              <div className="text-xs text-slate-500 mt-1">PDF, Word, PNG or JPG, up to 25 MB. A PDF gives the most exact checks.</div>
              <label className="inline-block mt-4 px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-50">
                Choose a file<input type="file" className="hidden" accept=".pdf,.docx,.png,.jpg,.jpeg" onChange={(e) => pick(e.target.files?.[0])} />
              </label>
            </div>
          )}
        </div>
        <label className="block mt-4">
          <span className="text-xs font-semibold text-slate-600">Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="What the piece is, in a few words" />
        </label>
        <p className="text-xs text-slate-500 mt-4">The moment it is uploaded you see the material on screen and watch it being read: each check, each finding where it points, each question the AI answers. Then the AI sets the facts the text makes certain, marketing or communication, the material type, the class, Medicaid content, each with the sentence it relied on, and brings you only what needs your judgment. The HPMS path, the reviewers and the dates follow. You can change anything.</p>
      </Card>

      <div className="grid gap-5">
        <Card title="Three questions">
          <div className="text-xs font-semibold text-slate-600">Who receives it?</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(AUDIENCE_WORDS).map(([k, label]) => (
              <button key={k} type="button" onClick={() => toggle(k)} className={`px-3 py-1.5 rounded-xl text-sm border ${audience.includes(k) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}>{label}</button>
            ))}
          </div>
          <label className="block mt-4">
            <span className="text-xs font-semibold text-slate-600">When do you want to start using it?</span>
            <input type="date" value={useDate} onChange={(e) => setUseDate(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="mt-4 text-xs font-semibold text-slate-600">Is it new, a change, or a translation?</div>
          <div className="mt-2 grid gap-2">
            {KINDS.map(([k, label]) => (
              <label key={k} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer ${kind === k ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}>
                <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} /> <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </div>
          {kind !== 'new' && (
            <label className="block mt-3">
              <span className="text-xs font-semibold text-slate-600">The existing material (its record ID from the Library)</span>
              <input value={parentId} onChange={(e) => setParentId(e.target.value.trim())} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono" placeholder="paste the record ID" />
            </label>
          )}
          {kind === 'translation' && (
            <label className="block mt-3">
              <span className="text-xs font-semibold text-slate-600">Language</span>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                {(boot.settings?.languages || []).map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </label>
          )}
        </Card>
        {boot.is_compliance && (
          <label className="flex items-center gap-2 text-xs text-slate-600 px-1">
            <input type="checkbox" checked={practice} onChange={(e) => setPractice(e.target.checked)} /> Practice material (kept apart from the real history; used to test the checks)
          </label>
        )}
        {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{err}</div>}
        <Button type="submit" disabled={busy}>{busy ? 'Uploading…' : 'Upload and read'}</Button>
      </div>
    </form>
  );
}
